import { useEffect, useState, type CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { PAYMENTS_ENABLED } from '@redevicer/shared'
import { offersAPI, paymentsAPI } from '../api/client'
import DealRatingForm from './DealRatingForm'

function IconOffers() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M7 7h10l-3-3M17 17H7l3 3"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M4 12h16"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeDasharray="3 3"
        opacity="0.55"
      />
    </svg>
  )
}

interface Offer {
  id: number
  listing_id: number
  offered_price: number
  is_accepted: boolean
  is_rejected: boolean
  created_at: string
  listing_title?: string | null
  offer_kind?: string
  exchange_offered_listing_model?: string | null
}

interface MineTransaction {
  id: number
  offer_id: number
  listing_id: number
  listing_title: string
  role: string
  amount: number
  platform_fee: number
  seller_receives: number
  status: string
  payment_id: string | null
  tracking_number?: string | null
  carrier?: string | null
  purchase_route?: string | null
  shipping_provider?: string | null
  shipping_locker_id?: string | null
  shipping_locker_name?: string | null
  shipping_dest_city?: string | null
  shipping_dest_postal?: string | null
  buyer_address_id?: number | null
  created_at: string
  counterparty_name?: string | null
  can_rate?: boolean
  my_rating_stars?: number | null
}

interface MyOffersProps {
  token: string
  onOpenOfferDetail?: (offerId: number) => void
}

const CANCELLABLE_STATUSES = new Set(['pending', 'accepted', 'shipped'])
const DISPUTE_STATUSES = new Set(['accepted', 'shipped', 'delivered'])

function DisputeBlock({
  tx,
  token,
  onDone,
}: {
  tx: MineTransaction
  token: string
  onDone: () => void
}) {
  const { t } = useTranslation()
  const [showForm, setShowForm] = useState(false)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  if (tx.role !== 'buyer' || !DISPUTE_STATUSES.has(tx.status)) return null

  function submitDispute() {
    const trimmed = reason.trim()
    if (trimmed.length < 3) {
      alert(t('deals.disputeReasonLabel'))
      return
    }
    setBusy(true)
    paymentsAPI
      .openDispute(tx.id, trimmed, token)
      .then(() => onDone())
      .catch((err) => alert(err.response?.data?.detail || err.message))
      .finally(() => setBusy(false))
  }

  if (!showForm) {
    return (
      <button type="button" className="btn-deal-action btn-deal-cancel" onClick={() => setShowForm(true)}>
        {t('deals.openDispute')}
      </button>
    )
  }

  return (
    <div className="deal-cancel-block">
      <label className="deal-ship-label">
        <span>{t('deals.disputeReasonLabel')}</span>
        <textarea
          className="deal-ship-input deal-cancel-reason"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t('deals.disputeReasonPlaceholder')}
        />
      </label>
      <div className="deal-cancel-actions">
        <button type="button" className="btn-text-refresh" onClick={() => setShowForm(false)}>
          {t('common.cancel')}
        </button>
        <button type="button" className="btn-deal-action btn-deal-cancel" disabled={busy} onClick={submitDispute}>
          {busy ? '…' : t('deals.disputeConfirm')}
        </button>
      </div>
    </div>
  )
}

function CancelDealBlock({
  tx,
  token,
  onDone,
}: {
  tx: MineTransaction
  token: string
  onDone: () => void
}) {
  const { t } = useTranslation()
  const [showForm, setShowForm] = useState(false)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  if (tx.status === 'refunded') {
    return <p className="meta-text deal-cancelled">{t('deals.cancelled')}</p>
  }
  if (!CANCELLABLE_STATUSES.has(tx.status)) return null

  function submitCancel() {
    const trimmed = reason.trim()
    if (trimmed.length < 3) {
      alert(t('deals.cancelReasonLabel'))
      return
    }
    setBusy(true)
    paymentsAPI
      .cancelTransaction(tx.id, trimmed, token)
      .then(() => onDone())
      .catch((err) => alert(err.response?.data?.detail || err.message))
      .finally(() => setBusy(false))
  }

  if (!showForm) {
    return (
      <button type="button" className="btn-deal-action btn-deal-cancel" onClick={() => setShowForm(true)}>
        {t('deals.cancelDeal')}
      </button>
    )
  }

  return (
    <div className="deal-cancel-block">
      <label className="deal-ship-label">
        <span>{t('deals.cancelReasonLabel')}</span>
        <textarea
          className="deal-ship-input deal-cancel-reason"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t('deals.cancelReasonPlaceholder')}
        />
      </label>
      <div className="deal-cancel-actions">
        <button type="button" className="btn-text-refresh" onClick={() => setShowForm(false)}>
          {t('common.cancel')}
        </button>
        <button type="button" className="btn-deal-action btn-deal-cancel" disabled={busy} onClick={submitCancel}>
          {busy ? '…' : t('deals.cancelConfirm')}
        </button>
      </div>
    </div>
  )
}

function defaultCarrierLabel(provider: string | null | undefined): string {
  if (!provider) return ''
  const map: Record<string, string> = {
    omniva: 'Omniva',
    dpd: 'DPD',
    lp_express: 'LP Express',
  }
  return map[provider] || provider.replace(/_/g, ' ')
}

function DealActions({
  tx,
  token,
  onDone,
  stripeCheckoutEnabled,
}: {
  tx: MineTransaction
  token: string
  onDone: () => void
  stripeCheckoutEnabled: boolean
}) {
  const { t } = useTranslation()
  const [busy, setBusy] = useState(false)
  const [carrierIn, setCarrierIn] = useState(() => defaultCarrierLabel(tx.shipping_provider))
  const [trackingIn, setTrackingIn] = useState('')

  function markShipped() {
    setBusy(true)
    const carrier = carrierIn.trim()
    const tracking_number = trackingIn.trim()
    paymentsAPI
      .markShipped(tx.id, token, {
        ...(carrier ? { carrier } : {}),
        ...(tracking_number ? { tracking_number } : {}),
      })
      .then(() => onDone())
      .catch((err) => alert(err.response?.data?.detail || err.message))
      .finally(() => setBusy(false))
  }

  function confirmDelivery() {
    setBusy(true)
    paymentsAPI
      .confirmDelivery(tx.id, token)
      .then(() => onDone())
      .catch((err) => alert(err.response?.data?.detail || err.message))
      .finally(() => setBusy(false))
  }

  function payStripe() {
    setBusy(true)
    paymentsAPI
      .createStripeCheckout(tx.id, token)
      .then((res) => {
        const url = res.data?.checkout_url as string | undefined
        if (url) window.location.href = url
        else alert(t('deals.checkoutNoUrl'))
      })
      .catch((err) => alert(err.response?.data?.detail || err.message))
      .finally(() => setBusy(false))
  }

  if (!PAYMENTS_ENABLED && tx.role === 'buyer' && tx.status === 'pending') {
    return null
  }

  if (tx.role === 'buyer' && tx.status === 'pending' && !stripeCheckoutEnabled) {
    return <p className="meta-text deal-pay-block">{t('deals.stripeNotConfigured')}</p>
  }

  if (tx.role === 'buyer' && tx.status === 'pending' && stripeCheckoutEnabled) {
    return (
      <div className="deal-pay-block">
        <p className="meta-text">{t('deals.awaitingPayment')}</p>
        <button type="button" className="btn-deal-action btn-deal-pay" disabled={busy} onClick={payStripe}>
          {busy ? '…' : t('deals.payWithStripe')}
        </button>
      </div>
    )
  }

  if (tx.role === 'buyer' && tx.status === 'accepted') {
    return (
      <>
        <p className="meta-text">{t('deals.waitingSellerShip')}</p>
        <DisputeBlock tx={tx} token={token} onDone={onDone} />
      </>
    )
  }

  if (tx.role === 'seller' && tx.status === 'accepted') {
    return (
      <div className="deal-ship-block">
        <p className="meta-text deal-manual-ship-sop">{t('deals.manualShippingSop')}</p>
        {tx.shipping_locker_name ? (
          <p className="meta-text">
            {t('deals.buyerLocker', { name: tx.shipping_locker_name })}
          </p>
        ) : null}
        <div className="deal-ship-fields">
          <label className="deal-ship-label">
            <span>{t('deals.carrier')}</span>
            <input
              type="text"
              className="deal-ship-input"
              value={carrierIn}
              onChange={(e) => setCarrierIn(e.target.value)}
              placeholder={t('deals.carrierPlaceholder')}
              autoComplete="off"
            />
          </label>
          <label className="deal-ship-label">
            <span>{t('deals.trackingNumber')}</span>
            <input
              type="text"
              className="deal-ship-input"
              value={trackingIn}
              onChange={(e) => setTrackingIn(e.target.value)}
              placeholder={t('deals.trackingPlaceholder')}
              autoComplete="off"
            />
          </label>
        </div>
        <button type="button" className="btn-deal-action btn-deal-ship" disabled={busy} onClick={markShipped}>
          {busy ? '…' : t('deals.markShipped')}
        </button>
      </div>
    )
  }

  if (tx.role === 'buyer' && tx.status === 'shipped') {
    return (
      <>
        <button type="button" className="btn-deal-action btn-deal-confirm" disabled={busy} onClick={confirmDelivery}>
          {busy ? '…' : t('deals.confirmDelivery')}
        </button>
        <DisputeBlock tx={tx} token={token} onDone={onDone} />
      </>
    )
  }

  if (tx.status === 'delivered') {
    return (
      <>
        <p className="meta-text deal-escrow-note">{t('deals.escrowRelease')}</p>
        <DealRatingForm
          txId={tx.id}
          token={token}
          counterpartyName={tx.counterparty_name}
          myRatingStars={tx.my_rating_stars}
          canRate={!!tx.can_rate}
          onDone={onDone}
        />
        <DisputeBlock tx={tx} token={token} onDone={onDone} />
      </>
    )
  }

  if (tx.status === 'completed') {
    return (
      <>
        <p className="deal-done">{t('deals.completed')}</p>
        <DealRatingForm
          txId={tx.id}
          token={token}
          counterpartyName={tx.counterparty_name}
          myRatingStars={tx.my_rating_stars}
          canRate={!!tx.can_rate}
          onDone={onDone}
        />
      </>
    )
  }

  if (tx.status === 'shipped' && tx.role === 'seller') {
    return <p className="meta-text">{t('deals.waitingBuyerConfirm')}</p>
  }

  return null
}

function MyOffers({ token, onOpenOfferDetail }: MyOffersProps) {
  const { t } = useTranslation()
  const [offers, setOffers] = useState<Offer[]>([])
  const [transactions, setTransactions] = useState<MineTransaction[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [stripeCheckoutEnabled, setStripeCheckoutEnabled] = useState(false)

  function txForOffer(offerId: number): MineTransaction | undefined {
    return transactions.find((x) => x.offer_id === offerId)
  }

  function reload() {
    setLoading(true)
    setError('')
    Promise.all([
      offersAPI.getMyOffers(token),
      paymentsAPI.getMyTransactions(token),
      paymentsAPI.getPublicConfig(),
    ])
      .then(([oRes, tRes, cfg]) => {
        setOffers(oRes.data)
        setTransactions(tRes.data)
        setStripeCheckoutEnabled(!!cfg.data?.stripe_checkout_enabled)
      })
      .catch((err) => setError(err.response?.data?.detail || 'Failed to load'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    reload()
  }, [token])

  const pendingOffers = offers.filter((o) => !o.is_accepted && !o.is_rejected)
  const rejectedOffers = offers.filter((o) => o.is_rejected)
  const isFullyEmpty = !loading && offers.length === 0 && transactions.length === 0

  return (
    <section className="my-offers-page my-offers-page--modern">
      <header className="my-offers-page__hero">
        <div className="my-offers-page__hero-glow" aria-hidden="true" />
        <div className="my-offers-page__hero-icon" aria-hidden="true">
          <IconOffers />
        </div>
        <h2>{t('menu.myOffers', 'My offers')}</h2>
        <p className="my-offers-page__hint">{t('myOffers.hint')}</p>
        <button type="button" className="my-offers-page__refresh" onClick={reload} disabled={loading}>
          {loading ? t('common.loading') : t('catalog.refresh')}
        </button>
      </header>

      <div className="my-offers-page__body">
        {error && <p className="my-offers-page__error error-text">{error}</p>}

        {isFullyEmpty && (
          <div className="my-offers-empty my-offers-empty--bloom">
            <div className="my-offers-empty__glow" aria-hidden="true" />
            <div className="my-offers-empty__icon" aria-hidden="true">
              <IconOffers />
            </div>
            <p className="my-offers-empty__title">{t('myOffers.empty')}</p>
            <p className="my-offers-empty__hint">{t('deals.noDealsYet')}</p>
          </div>
        )}

        {!isFullyEmpty && (
          <>
            <section className="my-offers-section">
              <h3 className="my-offers-section__title">{t('deals.sectionDeals')}</h3>
              {!loading && transactions.length === 0 && (
                <div className="my-offers-section__empty">
                  <p>{t('deals.noDealsYet')}</p>
                </div>
              )}
              <div className="my-offers-grid my-offers-grid--deals">
                {transactions.map((tx, idx) => (
                  <article
                    key={tx.id}
                    className="my-offers-card my-offers-card--deal offer-card offer-card--rich deal-card"
                    style={{ '--offer-i': idx } as CSSProperties}
                  >
                    <div className="my-offers-card__head">
                      <h4 className="my-offers-card__title">{tx.listing_title}</h4>
                      <span className={`my-offers-status my-offers-status--${tx.status}`}>{tx.status}</span>
                    </div>
                    <p className="my-offers-card__meta">
                      {t('deals.transaction')} #{tx.id}
                      {tx.role === 'buyer' ? ` · ${t('deals.youBuy')}` : ` · ${t('deals.youSell')}`}
                    </p>
                    <p className="my-offers-card__price">€{tx.amount.toFixed(2)}</p>
                    <p className="my-offers-card__meta">
                      {t('deals.platformFee')}: €{tx.platform_fee.toFixed(2)}
                    </p>
                    {tx.role === 'seller' && (
                      <p className="my-offers-card__highlight">{t('deals.youReceive')}: €{tx.seller_receives.toFixed(2)}</p>
                    )}
                    {tx.role === 'buyer' && tx.payment_id && (
                      <p className="my-offers-card__meta deal-stub">
                        {tx.payment_id.startsWith('stub_') ? t('deals.stubPayment') : t('deals.paymentReference')}:{' '}
                        {tx.payment_id}
                      </p>
                    )}
                    {(tx.status === 'shipped' || tx.status === 'completed') && (tx.carrier || tx.tracking_number) && (
                      <div className="my-offers-card__tracking deal-tracking-box">
                        {tx.carrier && (
                          <p className="my-offers-card__meta">
                            {t('deals.carrier')}: <strong>{tx.carrier}</strong>
                          </p>
                        )}
                        {tx.tracking_number && (
                          <p className="my-offers-card__meta">
                            {t('deals.trackingNumber')}: <strong>{tx.tracking_number}</strong>
                          </p>
                        )}
                      </div>
                    )}
                    <div className="my-offers-card__actions deal-actions-row">
                      <DealActions
                        tx={tx}
                        token={token}
                        onDone={reload}
                        stripeCheckoutEnabled={stripeCheckoutEnabled}
                      />
                      <CancelDealBlock tx={tx} token={token} onDone={reload} />
                    </div>
                  </article>
                ))}
              </div>
            </section>

            {pendingOffers.length > 0 && (
              <section className="my-offers-section">
                <h3 className="my-offers-section__title">{t('deals.sectionWaiting')}</h3>
                <div className="my-offers-grid">
                  {pendingOffers.map((offer, idx) => (
                    <article
                      key={offer.id}
                      className="my-offers-card my-offers-card--pending offer-card offer-card--rich"
                      style={{ '--offer-i': idx } as CSSProperties}
                    >
                      <div className="my-offers-card__head">
                        <h4 className="my-offers-card__title">
                          {offer.listing_title || `${t('deals.listing')} #${offer.listing_id}`}
                        </h4>
                        {offer.offer_kind === 'exchange' && (
                          <span className="offer-pill offer-pill--exchange">{t('offerDetail.exchangeBadge')}</span>
                        )}
                      </div>
                      <p className="my-offers-card__meta">{t('deals.yourOffer')}</p>
                      <p className="my-offers-card__price">€{offer.offered_price.toFixed(2)}</p>
                      {offer.exchange_offered_listing_model && (
                        <p className="my-offers-card__meta">{offer.exchange_offered_listing_model}</p>
                      )}
                      <div className="my-offers-card__status-row offer-status-line">
                        <span className="offer-pill offer-pill--wait">{t('offerStatus.pending')}</span>
                        <span className="my-offers-card__date">{new Date(offer.created_at).toLocaleString()}</span>
                      </div>
                      <button
                        type="button"
                        className="my-offers-card__btn"
                        onClick={() => onOpenOfferDetail?.(offer.id)}
                      >
                        {t('offerChats.openChat')}
                      </button>
                    </article>
                  ))}
                </div>
              </section>
            )}

            {rejectedOffers.length > 0 && (
              <section className="my-offers-section my-offers-section--muted">
                <h3 className="my-offers-section__title">{t('deals.sectionPassed')}</h3>
                <div className="my-offers-grid">
                  {rejectedOffers.map((offer, idx) => (
                    <article
                      key={offer.id}
                      className="my-offers-card my-offers-card--rejected offer-card offer-card--muted"
                      style={{ '--offer-i': idx } as CSSProperties}
                    >
                      <p className="my-offers-card__meta">
                        {offer.listing_title || `${t('deals.listing')} #${offer.listing_id}`}
                      </p>
                      <p className="my-offers-card__price my-offers-card__price--muted">€{offer.offered_price.toFixed(2)}</p>
                      <span className="offer-pill offer-pill--bad">{t('offerStatus.rejected')}</span>
                    </article>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </section>
  )
}

export default MyOffers
